import React, { useMemo, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Spinner,
  Table,
  ToggleSwitch,
  Progress,
} from "flowbite-react";
import { useQuery } from "react-query";
import { HiOutlineRefresh } from "react-icons/hi";
import { FiTrendingUp, FiDollarSign, FiUsers, FiAward } from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";
import DrawerLoading from "../components/DrawerLoading";
import ReactApexChart from "react-apexcharts";
import { useApplication } from "../contexts/ApplicationContext";
import AutoCompleteInput from "../components/inputs/Autocomplete";
import DateRangeInput from "../components/inputs/DatePickerInput"; // ajuste o path se necessário
import moment from "moment";
import "moment/locale/pt-br";

moment.locale("pt-br");

function numberBR(n) {
  const x = Number(n || 0);
  return x.toLocaleString("pt-BR");
}
function moneyBR(n) {
  const x = Number(n || 0);
  return x.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function pct(x) {
  const p = Number(x || 0) * 100;
  return `${p.toFixed(1)}%`;
}

function dateTimeBRSP(iso) {
  if (!iso) return "-";
  // renderiza no fuso America/Sao_Paulo, 24h
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour12: false,
  });
}

// Card com COR SÓLIDA (sem gradiente)
function StatCard({ title, value, icon: Icon, color = "indigo", isLoading }) {
  const colorMap = {
    indigo: "bg-indigo-600",
    emerald: "bg-gray-600",
    fuchsia: "bg-green-600",
    amber: "bg-green-500",
    sky: "bg-green-600",
    violet: "bg-green-600",
    slate: "bg-blue-500",
  };
  const bg = colorMap[color] || colorMap.indigo;

  return (
    <div className={`rounded-xl p-4 shadow-md text-white ${bg}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm/5 text-white/80">{title}</div>
        <div className="shrink-0 rounded-full bg-white/20 p-2">
          <Icon className="text-xl" />
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <div className="mt-2 text-2xl font-semibold tracking-tight">
          {value}
        </div>
        {isLoading && <Spinner className="mt-2" />}
      </div>
    </div>
  );
}

export default function ReviewDiario({ isActive = true }) {
  const [onlySuccess, setOnlySuccess] = useState(true);
  const { userPermission, user } = useApplication();

  // 🔹 Representante
  const [selectedRepresentative, setSelectedRepresentative] = useState({
    label: "Todos os representantes",
    value: "",
  });

  const { data: listRepresentatives, isLoading: isLoadingListRepresentatives } =
    useQuery(
      ["listRepresentatives"],
      async () => {
        const { data } = await axiosInstance.get(
          `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
        );
        const representatives = [
          { label: "Todos os representantes", value: "" },
          { label: "Relinq Score", value: 1 },
          ...data.data.data.map((r) => ({
            value: r.id,
            label: r?.name || r?.socialReason,
          })),
        ];
        return representatives;
      },
      { enabled: userPermission === "POWER_USER" }
    );

  // 🔹 Período (default = HOJE)
  const today = useMemo(() => new Date(), []);
  // rascunho (controla o DatePicker)
  const [draftRange, setDraftRange] = useState([today, today]);
  const [draftStart, draftEnd] = draftRange;
  // aplicado (controla as queries)
  const [appliedRange, setAppliedRange] = useState([today, today]);
  const [createdAtStart, createdAtEnd] = appliedRange;

  const startISO = createdAtStart
    ? moment(createdAtStart).format("YYYY-MM-DD")
    : "";
  const endISO = createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "";

  // Aplica o range apenas quando houver start & end (ou quando limpar)
  const handleRangeChange = (dates) => {
    const [start, end] = dates || [];
    setDraftRange([start, end]);

    // limpar -> volta para hoje (ou troque para [null, null] se quiser sem filtro)
    if (!start && !end) {
      setDraftRange([today, today]);
      setAppliedRange([today, today]);
      return;
    }

    // aplica apenas quando há os dois
    if (start && end) {
      setAppliedRange([start, end]);
    }
  };

  // ====== QUERIES ======
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    [
      "daily-overview",
      {
        onlySuccess,
        representativeId:
          userPermission === "POWER_USER"
            ? selectedRepresentative?.value || ""
            : "",
        createdAtStart: startISO,
        createdAtEnd: endISO,
      },
    ],
    async () => {
      const params = {
        ...(onlySuccess ? { status: "success" } : {}),
        ...(userPermission === "POWER_USER"
          ? { representativeId: selectedRepresentative?.value || "" }
          : {}),
        ...(startISO ? { createdAtStart: startISO } : {}),
        ...(endISO ? { createdAtEnd: endISO } : {}),
      };
      const res = await axiosInstance.get(ADMIN.GET_DAILY_INSIGHTS, { params });
      return res.data;
    },
    { enabled: !!isActive, refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const {
    data: pieDataRaw,
    isLoading: isLoadingPie,
    isFetching: isFetchingPie,
    error: errorPie,
    refetch: refetchPie,
  } = useQuery(
    [
      "daily-products-pie",
      {
        onlySuccess,
        representativeId:
          userPermission === "POWER_USER"
            ? selectedRepresentative?.value || ""
            : "",
        createdAtStart: startISO,
        createdAtEnd: endISO,
      },
    ],
    async () => {
      const params = {
        topN: 8,
        ...(onlySuccess ? { status: "success" } : {}),
        ...(userPermission === "POWER_USER"
          ? { representativeId: selectedRepresentative?.value || "" }
          : {}),
        ...(startISO ? { createdAtStart: startISO } : {}),
        ...(endISO ? { createdAtEnd: endISO } : {}),
      };
      const res = await axiosInstance.get(
        ADMIN.GET_DAILY_INSIGHTS_PRODUCTS_PIE,
        { params }
      );
      return res.data?.data || [];
    },
    { enabled: !!isActive, refetchOnWindowFocus: false, keepPreviousData: true }
  );

  // ====== PIE ======
  const pieSeries = useMemo(
    () => (pieDataRaw || []).map((d) => Number(d.value || 0)),
    [pieDataRaw]
  );
  const pieLabels = useMemo(
    () => (pieDataRaw || []).map((d) => d.name || "—"),
    [pieDataRaw]
  );
  const pieTotal = useMemo(
    () => pieSeries.reduce((acc, n) => acc + (Number(n) || 0), 0),
    [pieSeries]
  );

  const pieOptions = useMemo(
    () => ({
      chart: {
        type: "pie",
        animations: { enabled: true },
        toolbar: { show: false },
      },
      labels: pieLabels,
      legend: { position: "bottom", labels: { colors: "#374151" } },
      tooltip: {
        y: {
          formatter: (val) => {
            const pct = pieTotal ? ((val / pieTotal) * 100).toFixed(1) : "0.0";
            return `${numberBR(val)} consultas • ${pct}%`;
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        dropShadow: { enabled: false },
        style: { fontSize: "11px" },
      },
      colors: [
        "#6366F1",
        "#10B981",
        "#F59E0B",
        "#06B6D4",
        "#8B5CF6",
        "#EF4444",
        "#14B8A6",
        "#84CC16",
        "#F472B6",
        "#0EA5E9",
      ],
      responsive: [
        {
          breakpoint: 640,
          options: {
            legend: { position: "bottom" },
            dataLabels: { style: { fontSize: "10px" } },
          },
        },
      ],
    }),
    [pieLabels, pieTotal]
  );

  useEffect(() => {
    if (isActive) refetch(); // revalida quando a aba ativa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const totals = data?.totals || { total: 0, revenue: 0, cost: 0 };
  const revenue = Number(totals.revenue || 0);
  const cost = Number(totals.cost || 0);
  const margin = revenue - cost;

  const topQty = useMemo(
    () => (data?.clientsRankByQty || []).slice(0, 10),
    [data]
  );
  const topValue = useMemo(
    () =>
      (data?.valueSpentByClient || [])
        .slice()
        .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
        .slice(0, 10),
    [data]
  );
  const fullByQty = data?.clientsRankByQty || [];
  const newActives = data?.newActiveClientsToday || [];
  const topQtyCard = topQty?.[0];
  const topValueCard = topValue?.[0];

  const periodLabel =
    startISO && endISO
      ? `${moment(createdAtStart).format("DD/MM/YYYY")} — ${moment(
          createdAtEnd
        ).format("DD/MM/YYYY")}`
      : "Hoje";

  // condição para mostrar margem
  const isRepView =
    user?.Company?.companyTypeId === "REPRESENTANTE" ||
    (userPermission === "POWER_USER" && !!selectedRepresentative?.value);

  const newActivesSorted = useMemo(
    () =>
      (newActives || [])
        .slice()
        .sort((a, b) => new Date(b.firstAt) - new Date(a.firstAt)),
    [newActives]
  );

  const repNameById = useMemo(() => {
    const map = {};
    (listRepresentatives || []).forEach((r) => {
      // sua lista tem { label, value }
      if (r.value) map[r.value] = r.label;
    });
    // também nomeie o id 1 “Relinq Score” se vier a partir do servidor
    map[1] ||= "Relinq Score";
    return map;
  }, [listRepresentatives]);

  // agrega por representativeId usando os itens das empresas
  const repsAgg = useMemo(() => {
    const base = {};
    const pushRow = (row) => {
      const repId = row?.representativeId || 0;
      if (!repId) return;

      if (!base[repId]) {
        base[repId] = {
          representativeId: repId,
          representativeName: repNameById[repId] || `Rep ${repId}`,
          consultationsCount: 0,
          revenue: 0,
          cost: 0,
        };
      }
      base[repId].consultationsCount += Number(row.consultationsCount || 0);
      base[repId].revenue += Number(row.totalSpent || 0);
      base[repId].cost += Number(row.cost || 0);
    };

    (data?.clientsRankByQty || []).forEach(pushRow); // para contagem
    (data?.valueSpentByClient || []).forEach(() => {}); // já somamos revenue/cost acima

    // calcula margem e % da receita total
    const totalRevenue = Number(data?.totals?.revenue || 0);
    const arr = Object.values(base).map((r) => ({
      ...r,
      margin: r.revenue - r.cost,
      shareOfRevenue: totalRevenue ? r.revenue / totalRevenue : 0,
    }));

    // ordenações úteis
    const byQty = arr
      .slice()
      .sort((a, b) => b.consultationsCount - a.consultationsCount);
    const byRevenue = arr.slice().sort((a, b) => b.revenue - a.revenue);
    const byMargin = arr
      .slice()
      .sort((a, b) => (b.margin || 0) - (a.margin || 0));

    return { arr, byQty, byRevenue, byMargin };
  }, [data, repNameById]);

  return (
    <div className="space-y-5 bg-white p-2 rounded-lg p-2 md:p-4">
      {/* Header / filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-indigo-700">
            Review de consultas
          </h2>
          <p className="text-sm text-gray-500 truncate">
            Fuso: America/Sao_Paulo • Atualizado:{" "}
            {data?.generatedAt
              ? new Date(data.generatedAt).toLocaleString("pt-BR")
              : "-"}
          </p>
          <p className="text-sm text-gray-500">Período: {periodLabel}</p>
        </div>

        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={!!onlySuccess}
            label="Somente sucesso"
            onChange={setOnlySuccess}
          />
          <Button
            onClick={() => {
              refetch();
              refetchPie();
            }}
            gradientMonochrome="info"
            outline
          >
            {isFetching || isFetchingPie ? (
              <span className="inline-flex items-center gap-2">
                <Spinner size="sm" /> Atualizando...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <HiOutlineRefresh className="text-base" /> Atualizar
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Linha de filtros adicionais */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-72">
          <DateRangeInput
            label="Período"
            startDate={draftStart}
            endDate={draftEnd}
            onChange={handleRangeChange}
            maxDate={new Date()}
            isClearable
          />
        </div>

        {userPermission === "POWER_USER" && (
          <div className="w-72">
            <AutoCompleteInput
              options={listRepresentatives || []}
              label="Representante"
              placeholder="Todos os representantes"
              onChange={(rep) => setSelectedRepresentative(rep)}
              value={selectedRepresentative?.value}
              isLoading={isLoadingListRepresentatives}
            />
          </div>
        )}
      </div>

      {error && (
        <Alert color="failure">
          <span className="font-medium">Erro:</span> Não foi possível carregar o
          review.
        </Alert>
      )}

      {/* Cards com cores sólidas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Consultas no período"
          value={numberBR(totals.total)}
          icon={FiTrendingUp}
          color="indigo"
          isLoading={isLoading || isFetching}
        />
        <StatCard
          title="Receita no período"
          value={moneyBR(revenue)}
          icon={FiDollarSign}
          color="emerald"
          isLoading={isLoading || isFetching}
        />

        {/* Margem (somente para representante selecionado/visão de representante) */}
        {isRepView && (
          <StatCard
            title="Margem no período"
            value={moneyBR(margin)}
            icon={FiTrendingUp}
            color="slate"
            isLoading={isLoading || isFetching}
          />
        )}

        <div className="rounded-xl p-4 shadow-md ring-1 ring-black/5 bg-green-600 text-white">
          <div className="flex items-center justify-between">
            <div className="text-sm/5 text-white/80">Top por quantidade</div>
            <div className="shrink-0 rounded-full bg-white/20 p-2">
              <FiUsers className="text-xl" />
            </div>
          </div>
          <div className="mt-2 text-base font-semibold truncate">
            {topQtyCard
              ? topQtyCard.companyName || `ID ${topQtyCard.companyId}`
              : "-"}
          </div>
          <div className="text-sm text-white/90">
            {topQtyCard ? numberBR(topQtyCard.consultationsCount) : 0} consultas
          </div>
        </div>

        <div className="rounded-xl p-4 shadow-md ring-1 ring-black/5 bg-amber-500 text-white">
          <div className="flex items-center justify-between">
            <div className="text-sm/5 text-white/80">Top por valor</div>
            <div className="shrink-0 rounded-full bg-white/20 p-2">
              <FiAward className="text-xl" />
            </div>
          </div>
          <div className="mt-2 text-base font-semibold truncate">
            {topValueCard
              ? topValueCard.companyName || `ID ${topValueCard.companyId}`
              : "-"}
          </div>
          <div className="text-sm text-white/90">
            {moneyBR(topValueCard?.totalSpent || 0)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Top 10 - quantidade */}
        <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              Top 10 por quantidade
            </h3>
            {(isLoading || isFetching) && <DrawerLoading />}
          </div>
          <div className="overflow-x-auto">
            <Table className="w-full table-fixed" striped>
              <Table.Head>
                <Table.HeadCell>Cliente</Table.HeadCell>
                <Table.HeadCell className="text-right">
                  Consultas
                </Table.HeadCell>
                <Table.HeadCell className="text-right">Valor</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {topQty.map((row, idx) => (
                  <Table.Row key={`${row.companyId}-${idx}`}>
                    <Table.Cell className="min-w-0 py-1">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          <div
                            className="truncate font-medium text-xs uppercase"
                            style={{ maxWidth: "230px" }}
                          >
                            {row.companyName || `ID ${row.companyId}`}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            {pct(row.shareOfQueries)} das consultas
                            <div className="w-24">
                              <Progress
                                progress={Math.round(
                                  (row.shareOfQueries || 0) * 100
                                )}
                                size="sm"
                                color="indigo"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-right whitespace-nowrap">
                      {numberBR(row.consultationsCount)}
                    </Table.Cell>
                    <Table.Cell className="text-right whitespace-nowrap">
                      {moneyBR(row.totalSpent)}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {topQty.length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={3}
                      className="text-center text-gray-500"
                    >
                      Sem dados no período.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </div>

        {/* Top 10 - valor */}
        <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              Top 10 por valor
            </h3>
            {(isLoading || isFetching) && <DrawerLoading />}
          </div>
          <div className="overflow-x-auto">
            <Table className="w-full table-fixed" striped>
              <Table.Head>
                <Table.HeadCell>Cliente</Table.HeadCell>
                <Table.HeadCell className="text-right">
                  Consultas
                </Table.HeadCell>
                <Table.HeadCell className="text-right">Valor</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {topValue.map((row, idx) => (
                  <Table.Row key={`${row.companyId}-${idx}`}>
                    <Table.Cell className="min-w-0 py-1">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          <div
                            className="truncate font-medium text-xs"
                            style={{ maxWidth: "230px" }}
                          >
                            {row.companyName || `ID ${row.companyId}`}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            {pct(row.shareOfRevenue)} da receita
                            <div className="w-24">
                              <Progress
                                progress={Math.round(
                                  (row.shareOfRevenue || 0) * 100
                                )}
                                size="sm"
                                color="yellow"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-right whitespace-nowrap">
                      {numberBR(row.consultationsCount)}
                    </Table.Cell>
                    <Table.Cell className="text-right whitespace-nowrap">
                      {moneyBR(row.totalSpent)}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {topValue.length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={3}
                      className="text-center text-gray-500"
                    >
                      Sem dados no período.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      </div>

      {/* Novos clientes ativos no período + Pie */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              Novos clientes ativos no período ({newActivesSorted?.length})
            </h3>
            {(isLoading || isFetching) && <DrawerLoading />}
          </div>

          {newActivesSorted.length === 0 ? (
            <div className="text-gray-500 h-full flex items-center justify-center font-semibold py-5">
              Nenhum novo cliente no período.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto pr-1">
              <Table striped className="w-full">
                <Table.Head>
                  <Table.HeadCell>Cliente</Table.HeadCell>
                  <Table.HeadCell className="text-right">
                    Primeira consulta
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {newActivesSorted.map((c) => (
                    <Table.Row key={c.companyId}>
                      <Table.Cell className="min-w-0 py-2">
                        <div className="truncate" style={{ maxWidth: 250 }}>
                          {c.companyName || `ID ${c.companyId}`}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-right whitespace-nowrap py-2">
                        {dateTimeBRSP(c.firstAt)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              Consultas por produto (período)
            </h3>
            <div className="flex items-center gap-2">
              {(isLoadingPie || isFetchingPie) && <Spinner size="sm" />}
              <Button size="xs" onClick={() => refetchPie()} color="light">
                Atualizar
              </Button>
            </div>
          </div>

          {errorPie && (
            <Alert color="failure" className="mb-2">
              <span className="font-medium">Erro:</span> Não foi possível
              carregar o pie de produtos.
            </Alert>
          )}

          {(pieDataRaw || []).length === 0 ? (
            <div className="text-gray-500">Sem dados no período.</div>
          ) : (
            <div className="w-full">
              <ReactApexChart
                options={pieOptions}
                series={pieSeries}
                type="pie"
                height={340}
              />
            </div>
          )}

          {isLoadingPie && <DrawerLoading />}
        </div>
      </div>

      {/* Lista completa por quantidade */}
      <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-800">
            Todos os clientes (por quantidade)
          </h3>
          {(isLoading || isFetching) && <DrawerLoading />}
        </div>
        <div className="overflow-x-auto max-h-[500px] pr-2 overflow-y-scroll">
          <Table className="w-full table-fixed" striped>
            <colgroup>
              <col className="w-16" />
              <col />
              <col className="w-36" />
              <col className="w-44" />
            </colgroup>
            <Table.Head>
              <Table.HeadCell>#</Table.HeadCell>
              <Table.HeadCell>Cliente</Table.HeadCell>
              <Table.HeadCell className="text-right">Consultas</Table.HeadCell>
              <Table.HeadCell className="text-right">Valor</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {fullByQty.map((row, idx) => (
                <Table.Row key={`${row.companyId}-${idx}`}>
                  <Table.Cell className="whitespace-nowrap py-1">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-slate-700">
                      {idx + 1}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="min-w-0 py-1">
                    <div className="truncate font-medium text-xs mb-1">
                      {row.companyName || `ID ${row.companyId}`}
                      {row.representativeId && (
                        <>
                          {" | "}
                          <div
                            color="gray"
                            className="cursor-pointer underline text-blue-700 inline-block ml-1"
                            onClick={() => {
                              if (userPermission === "POWER_USER") {
                                setSelectedRepresentative({
                                  value: row.representativeId,
                                  label:
                                    repNameById[row.representativeId] ||
                                    `Rep ${row.representativeId}`,
                                });
                              }
                            }}
                            title="Filtrar por este representante"
                          >
                            Rep:{" "}
                            {repNameById[row.representativeId] ||
                              row.representativeId}{" "}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <Badge color="indigo" className="whitespace-nowrap">
                        {pct(row.shareOfQueries)} das consultas
                      </Badge>
                      <div className="w-28">
                        <Progress
                          progress={Math.round((row.shareOfQueries || 0) * 100)}
                          size="sm"
                          color="indigo"
                        />
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-right whitespace-nowrap">
                    {numberBR(row.consultationsCount)}
                  </Table.Cell>
                  <Table.Cell className="text-right whitespace-nowrap">
                    {moneyBR(row.totalSpent)}
                  </Table.Cell>
                </Table.Row>
              ))}
              {fullByQty.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={4} className="text-center text-gray-500">
                    Sem dados no período.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>
      <div>
        {/* === VISÃO POR REPRESENTANTE === */}
        {userPermission === "POWER_USER" && (
          <div className="border border-gray-200 shadow-sm p-4 rounded-md relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800">
                Representantes no período ({repsAgg.arr.length})
              </h3>
              {(isLoading || isFetching) && <DrawerLoading />}
            </div>

            <div className="overflow-x-auto">
              <Table className="w-full table-fixed" striped>
                <Table.Head>
                  <Table.HeadCell>Representante</Table.HeadCell>
                  <Table.HeadCell className="text-right">
                    Consultas
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right">
                    Receita
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right">Custo</Table.HeadCell>
                  {isRepView && (
                    <Table.HeadCell className="text-right">
                      Margem
                    </Table.HeadCell>
                  )}
                  <Table.HeadCell className="text-right">
                    % Receita
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {repsAgg.byRevenue.map((r) => (
                    <Table.Row key={r.representativeId}>
                      <Table.Cell className="min-w-0 py-1">
                        <div className="flex items-center gap-2">
                          <div
                            className="truncate font-medium"
                            style={{ maxWidth: 240 }}
                          >
                            {r.representativeName}
                          </div>
                          <Badge
                            color="gray"
                            className="cursor-pointer"
                            onClick={() => {
                              if (userPermission === "POWER_USER") {
                                setSelectedRepresentative({
                                  value: r.representativeId,
                                  label: r.representativeName,
                                });
                              }
                            }}
                          >
                            Filtrar
                          </Badge>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {numberBR(r.consultationsCount)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {moneyBR(r.revenue)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {moneyBR(r.cost)}
                      </Table.Cell>
                      {isRepView && (
                        <Table.Cell className="text-right">
                          {moneyBR(r.margin)}
                        </Table.Cell>
                      )}
                      <Table.Cell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <span>{pct(r.shareOfRevenue)}</span>
                          <div className="w-20">
                            <Progress
                              progress={Math.round(
                                (r.shareOfRevenue || 0) * 100
                              )}
                              size="sm"
                              color="yellow"
                            />
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  {repsAgg.arr.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={isRepView ? 6 : 5}
                        className="text-center text-gray-500"
                      >
                        Sem dados no período.
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
